import { useContext, useEffect, useState } from 'react'
import { apiGet } from '../../Api_Functions'
import { UserContext } from '../../../../context/UserContextProvider'

/**
 * Clave del ajuste, en la misma ruta de ajustes que Jupiter ya usa para `USE_WORKFLOW_INDEX`
 * (`settings/{KEY}`, sin endpoint propio). Sun pone la fila; aquí solo se lee.
 */
export const FABRICATION_ORDER_DETAIL_SETTING_KEY = 'USE_FABRICATION_ORDER_DETAIL'

/**
 * ADDENDUM 14 (E20) — el interruptor que decide si el listado de OFs usa la ventana NUEVA (el
 * detalle en modal) o la ANTIGUA (edición en línea y las nueve acciones de fila). Temporal, hasta
 * que los clientes se adapten.
 *
 * Tres reglas, y las tres son de seguridad, no de estilo:
 *
 *   1. **Nace apagado.** Una instalación que se actualiza sin tocar nada no nota ningún cambio: el
 *      día del merge la opción existe, pero no cambia la forma de trabajar de nadie.
 *   2. ⚠️ **Si no se puede leer, gana la ANTIGUA.** Un fallo de red, un backend viejo sin la fila o
 *      un cuerpo que no dice `true` dejan el interruptor apagado. Esto es lo contrario de lo
 *      habitual —normalmente lo nuevo es el camino feliz—, y es deliberado: **un fallo de red no
 *      puede cambiarle a nadie la forma de trabajar**. Quien no ha pedido la ventana nueva no debe
 *      encontrársela porque una petición se perdió.
 *   3. **Nunca rompe la ventana que lo pregunta.** El error se traga a propósito: el peor desenlace
 *      aceptable es seguir como se estaba, no un listado que no carga.
 *
 * Y una advertencia sobre el valor: Sun sirve `{ value: 'true' }` como CADENA, no como booleano, así
 * que la comparación es contra el texto. Un `!!setting.value` diría `true` para la cadena `'false'`.
 *
 * ═══ S406/S407 — POR QUÉ ESPERA AL USUARIO Y POR QUÉ REINTENTA ═══
 *
 * ⚠️⚠️ ESTE EFECTO SE DISPARABA AL MONTAR, A CIEGAS, CON `[]` DE DEPENDENCIAS. Dos defectos
 * encadenados, y el segundo es peor que el primero:
 *
 *   · **S406 — salía sin credencial.** La ruta de ajustes EXIGE autenticación (comprobado: pedir
 *     `settings/character` sin sesión devuelve 401, aunque no tenga `@PreAuthorize`). Al montar, el
 *     token podía no estar todavía donde `apiGet` lo lee, así que la petición salía sin él y Sun
 *     respondía 401 — mientras las llamadas de alrededor, disparadas un instante después, iban bien.
 *     Se vio exactamente así en un registro de red: 439 peticiones correctas y solo ESTA en 401.
 *
 *   · **S407 — y no se reintentaba NUNCA.** Con `[]`, un único intento. El `catch` apagaba el
 *     interruptor y ahí se quedaba TODA LA SESIÓN. Ese es el defecto de verdad: el detalle
 *     desaparecía y no volvía solo.
 *
 * 📌 LA REGLA 2 NO ESTABA MAL; ESTABA MAL ACOMPAÑADA. "Lo que no se puede leer se comporta como
 * apagado" sigue siendo el respaldo correcto y NO se toca. Lo que cambia es que "no se puede leer"
 * deje de incluir "todavía no había sesión": un fallo transitorio no puede volverse permanente.
 *
 * ⚠️ Y esto importa más de lo que parece porque se dispara solo: **cada reinicio del backend
 * invalida las sesiones** (el mapa de sesiones vive en memoria). Sin el reintento, cualquiera que
 * tuviera el listado abierto durante un reinicio perdía la ventana nueva hasta recargar, sin ningún
 * aviso y sin forma de relacionarlo con la causa.
 *
 * 📌 LA DEPENDENCIA ES `hasUser`, UN BOOLEANO, Y NO EL OBJETO `user`. Con el objeto, cualquier
 * cambio de identidad —un cambio de idioma, un refresco del contexto— relanzaría la petición sin
 * que haya ninguna pregunta nueva que hacer. Lo que este efecto necesita saber es exactamente una
 * cosa: si ya hay con qué autenticarse. El reintento ES esa transición de `false` a `true`, así que
 * no hace falta ni contador de intentos ni temporizador.
 *
 * @returns {boolean} Si el listado debe usar la ventana nueva (el detalle en modal)
 */
const useFabricationOrderDetailSetting = () => {
  const [enabled, setEnabled] = useState(false)
  // ⚠️⚠️ EL CONTEXTO DIRECTO Y NO `useUser()`, que es lo que CLAUDE.md recomienda y lo que escribí
  // primero. Me desvío a sabiendas por dos razones medidas, no por preferencia:
  //
  //   · `useUser` NO ES UN ACCESOR: LANZA sin proveedor ("useUser must be used within a
  //     UserProvider"). Metido aquí, cualquiera que monte `FabricationOrder` sin envolverlo en el
  //     contexto revienta — comprobado, tumbaba 12 suites de `configuration/orders` que no
  //     prueban nada de sesión. Un interruptor de presentación no puede volver obligatorio un
  //     proveedor que antes no hacía falta.
  //   · Y monta un `setInterval` de 1 s por consumidor. Este hook solo necesita saber SI hay
  //     sesión; no necesita vigilarla.
  //
  // El `|| {}` es el mismo patrón que ya usa `AssistantChatDrawer`, así que no se inventa nada. Sin
  // proveedor se comporta como "no hay sesión", que es el lado seguro: gana la ventana antigua.
  const { user } = useContext(UserContext) || {}
  const hasUser = !!user

  useEffect(() => {
    // S406: sin sesión no se pregunta. No es una optimización — preguntar aquí GARANTIZA un 401, y
    // con el respaldo de la regla 2 ese 401 se lee como "el ajuste está apagado", que es falso.
    if (!hasUser) return undefined

    let subscribed = true
    const loadSetting = async () => {
      try {
        const setting = await apiGet(`settings/${FABRICATION_ORDER_DETAIL_SETTING_KEY}`)
        if (subscribed) {
          setEnabled(setting?.value?.toLowerCase() === 'true')
        }
      } catch (error) {
        // Regla 2, intacta: lo que no se puede leer se comporta como apagado, y se queda como
        // estaba. Ahora bien, esto ya solo se alcanza CON sesión, así que es un fallo de verdad.
        if (subscribed) {
          setEnabled(false)
        }
      }
    }
    loadSetting()
    return () => { subscribed = false }
  }, [hasUser])

  return enabled
}

export default useFabricationOrderDetailSetting
